import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";

/**
 * Blocks the room until the viewer has voted on the currently-pinned poll.
 * Bypassed when: no pinned/active poll exists, viewer already voted, viewer
 * created the poll, or viewer is admin.
 */
export default function PollGate({ slug, user }) {
  const [poll, setPoll] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user) { setChecked(true); return; }
    let alive = true;
    api.get(`/rooms/${encodeURIComponent(slug)}/polls`)
      .then(({ data }) => {
        if (!alive) return;
        const pinned = (data.polls || []).find((p) => p.is_pinned && p.is_active);
        // Bypass: no pinned poll, already voted, own poll, or admin.
        if (!pinned || pinned.my_vote || pinned.created_by === user.id || user.is_admin) {
          setPoll(null);
        } else {
          setPoll(pinned);
        }
      })
      .catch(() => { /* silent — network failure shouldn't lock the room */ })
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [slug, user?.id, user?.is_admin]);

  const vote = async (option_id) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/polls/${poll.id}/vote`, { option_id });
      setPoll(null);
      toast.success("Vote counted — enjoy the room");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record vote.");
    } finally {
      setBusy(false);
    }
  };

  if (!checked || !poll) return null;

  return (
    <div
      className="fixed inset-0 z-[75] bg-obsidian/90 backdrop-blur-md flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="poll-gate-modal"
    >
      <div className="w-full max-w-md bg-obsidian border-2 border-acid p-6 md:p-8">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-acid uppercase">
          <BarChart3 className="w-3 h-3" /> Pinned poll · vote to enter
        </div>
        <h2 className="font-heading text-2xl md:text-3xl leading-snug mt-2" data-testid="poll-gate-question">
          {poll.question}
        </h2>
        <div className="font-mono text-[10px] text-white/40 mt-1">
          @{poll.creator_username} · {poll.total_votes} vote{poll.total_votes === 1 ? "" : "s"} so far
        </div>

        <div className="mt-5 space-y-2" data-testid="poll-gate-options">
          {poll.options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              disabled={busy}
              onClick={() => vote(opt.id)}
              data-testid={`poll-gate-option-${opt.id}`}
              className="w-full text-left border-2 border-white/15 hover:border-acid hover:bg-acid/5 px-4 py-3 font-body text-sm text-white/90 disabled:opacity-50 transition-colors"
            >
              {opt.text}
            </button>
          ))}
        </div>

        <div className="mt-5 font-mono text-[10px] text-white/40 leading-relaxed">
          // one vote per person · results reveal after you pick
        </div>
      </div>
    </div>
  );
}
